import { secureStorage } from '../../storage/secureStorage.js';
import { bookmarks } from '../bookmarks-store.js';
import { history } from '../history-store.js';
import { highlights } from '../highlights-store.js';
import { notes } from '../notes-store.js';
import { cards } from '../memorization-store.js';
import { earnedBadges } from '../badges-store.js';
import { heardChapters, dailyLog, freezeTokens, frozenDays } from '../progress-store.js';
import { playerSettings } from '../player-settings.js';
import { readingPrefs } from '../reading-prefs.js';
import { currentBible } from '../bible-store.js';
import { savedPlaylists } from '../playlist-store.js';
import { notificationPrefs } from '../notifications-store.js';
import { atlasJumps } from '../engagement-store.js';
import { activePlan } from '../reading-plans-store.js';
import { colorProfile } from '../color-profile.js';

export const STORAGE_KEY_MAP: Record<string, string> = {
  'fcbh-reader-bookmarks.v1': '_bm1',
  'fcbh-reader-history.v1': '_h1',
  'fcbh-reader-highlights.v1': '_hl1',
  'fcbh-reader-notes.v1': '_nt1',
  'fcbh-reader-memorization.v1': '_mm1',
  'fcbh-reader-badges.v1': '_bg1',
  'fcbh-reader-heard.v1': '_hd1',
  'fcbh-reader-daily-log.v1': '_dl1',
  'fcbh-reader-freeze-tokens.v1': '_ft1',
  'fcbh-reader-frozen-days.v1': '_fd1',
  'fcbh-reader-player-settings.v1': '_ps1',
  'fcbh-reader-reading-prefs.v2': '_rf2',
  'fcbh-reader-bible.v1': '_b1',
  'fcbh-reader-playlists.v1': '_pl1',
  'fcbh-reader-notifications.v1': '_nc1',
  'fcbh-reader-notifications-snooze.v1': '_ns1',
  'fcbh-reader-atlas-jumps.v1': '_aj1',
  'fcbh-reader-active-plan.v1': '_ap1',
  'fcbh-reader-color-profile.v2': '_cp2',
  'fcbh-reader-reading-position.v1': '_rp1',
  'fcbh-reader-resume.v1': '_rs1',
};

async function migrateKey(oldKey: string, newKey: string): Promise<void> {
  const raw = localStorage.getItem(oldKey);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      'iv' in parsed &&
      'ct' in parsed
    ) {
      return;
    }
  } catch {
    // not JSON — treat as plain string
  }
  await secureStorage.setItem(newKey, raw);
  localStorage.removeItem(oldKey);
}

async function readDecrypted(newKey: string): Promise<unknown | null> {
  const raw = await secureStorage.getItem(newKey);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return raw;
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isArray(v: unknown): v is unknown[] {
  return Array.isArray(v);
}

export async function hydrateStores(): Promise<void> {
  for (const [oldKey, newKey] of Object.entries(STORAGE_KEY_MAP)) {
    await migrateKey(oldKey, newKey);
  }

  const [
    bm, hist, hl, ns, mem, bdg,
    hrd, dl, ft, fd, ps, rp, bbl, pl, notif, aj, ap, cp,
  ] = await Promise.all([
    readDecrypted('_bm1'),
    readDecrypted('_h1'),
    readDecrypted('_hl1'),
    readDecrypted('_nt1'),
    readDecrypted('_mm1'),
    readDecrypted('_bg1'),
    readDecrypted('_hd1'),
    readDecrypted('_dl1'),
    readDecrypted('_ft1'),
    readDecrypted('_fd1'),
    readDecrypted('_ps1'),
    readDecrypted('_rf2'),
    readDecrypted('_b1'),
    readDecrypted('_pl1'),
    readDecrypted('_nc1'),
    readDecrypted('_aj1'),
    readDecrypted('_ap1'),
    readDecrypted('_cp2'),
  ]);

  if (isArray(bm)) bookmarks.value = bm as typeof bookmarks.value;
  if (isArray(hist)) history.value = hist as typeof history.value;

  if (isRecord(hl)) highlights.value = hl as typeof highlights.value;

  if (isRecord(ns)) notes.value = ns as typeof notes.value;

  if (isRecord(mem)) cards.value = mem as typeof cards.value;

  if (isRecord(bdg)) earnedBadges.value = bdg as typeof earnedBadges.value;

  if (isRecord(hrd)) heardChapters.value = hrd as typeof heardChapters.value;

  if (isRecord(dl)) dailyLog.value = dl as typeof dailyLog.value;

  if (typeof ft === 'number') freezeTokens.value = ft;
  else if (isArray(ft) && typeof ft[0] === 'number') freezeTokens.value = ft[0] as number;

  if (isArray(fd)) frozenDays.value = fd as string[];

  if (isRecord(ps)) playerSettings.value = { ...playerSettings.value, ...(ps as Partial<typeof playerSettings.value>) };

  if (isRecord(rp)) readingPrefs.value = { ...readingPrefs.value, ...(rp as Partial<typeof readingPrefs.value>) };

  if (isRecord(bbl)) currentBible.value = { ...currentBible.value, ...(bbl as Partial<typeof currentBible.value>) };

  if (isArray(pl)) savedPlaylists.value = pl as typeof savedPlaylists.value;

  if (isRecord(notif)) notificationPrefs.value = { ...notificationPrefs.value, ...(notif as Partial<typeof notificationPrefs.value>) };

  if (typeof aj === 'number') atlasJumps.value = aj;

  if (ap !== null) activePlan.value = ap as typeof activePlan.value;

  if (typeof cp === 'string') colorProfile.value = cp as typeof colorProfile.value;
}
